import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Copy, Flame, TrendingUp, Gift, Flag, Server, Users, ArrowUp, ArrowDown, MapPin, Radio, DollarSign, Shield, Target, Gamepad2, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DogTag, BannerStrip, RARITY, FactionCrest } from "../components/Cosmetics";
import PlayerActivity from "../components/PlayerActivity";
import DonationThermometer from "../components/DonationThermometer";
import DiscordCard from "../components/DiscordCard";
import RoleRanksStrip from "../components/RoleRanksStrip";
import FeaturedYouTube from "../components/FeaturedYouTube";
import LiveKillTicker from "../components/LiveKillTicker";
import LootTicker from "../components/LootTicker";
import { JoinModal, SERVER_ID } from "../components/JoinModal";

const IMG = {
  hero: "https://images.unsplash.com/flagged/photo-1560177776-55a762c5c000?crop=entropy&cs=srgb&fm=webp&q=55&w=1280",
  soldier: "https://images.unsplash.com/photo-1630534658718-395efda906cb?crop=entropy&cs=srgb&fm=webp&q=70&w=900",
  helmet: "https://images.unsplash.com/photo-1595472968262-48209bf5b390?crop=entropy&cs=srgb&fm=webp&q=70&w=900",
  weapon: "https://images.unsplash.com/photo-1610165539750-051008c3e799?crop=entropy&cs=srgb&fm=webp&q=70&w=900",
  rifle: "https://images.unsplash.com/photo-1521726834835-4ff1624e69da?crop=entropy&cs=srgb&fm=webp&q=70&w=900",
  sand: "https://images.unsplash.com/photo-1627817227571-91d46ce1194e?crop=entropy&cs=srgb&fm=webp&q=70&w=900",
  scope: "https://images.unsplash.com/photo-1482649671545-bc53dcf1ad7c?crop=entropy&cs=srgb&fm=webp&q=70&w=900",
  wallpaper: "https://images.unsplash.com/flagged/photo-1560177776-295b9cd779de?crop=entropy&cs=srgb&fm=webp&q=70&w=900",
};

const TILES = [
  { to: "/database", label: "Database", img: IMG.weapon, id: "database" },
  { to: "/map", label: "Interactive Maps", img: IMG.sand, id: "maps", hot: true },
  { to: "/compare", label: "Compare Items", img: IMG.rifle, id: "compare" },
  { to: "/season", label: "Season Pass", img: IMG.wallpaper, id: "season", hot: true },
  { to: "/leaderboard", label: "Leaderboards", img: IMG.helmet, id: "leaderboard" },
  { to: "/loadouts", label: "Loadouts", img: IMG.scope, id: "loadouts" },
  { to: "/loot", label: "Loot Drops", img: IMG.weapon, id: "loot" },
  { to: "/guides", label: "Guides", img: IMG.soldier, id: "guides" },
  { to: "/blog", label: "Blog", img: IMG.wallpaper, id: "blog" },
  { to: "/store", label: "Store", img: IMG.helmet, id: "store", hot: true },
  { to: "/clans", label: "Clans", img: IMG.soldier, id: "clans" },
  { to: "/team", label: "Staff Team", img: IMG.scope, id: "staff" },
  { to: "/events", label: "Events", img: IMG.sand, id: "events" },
  { to: "/seeding", label: "Seed Levels", img: IMG.rifle, id: "seeding" },
  { to: "/bans", label: "Ban List", img: IMG.weapon, id: "bans" },
  { to: "/clips", label: "Clips", img: IMG.scope, id: "clips" },
  { to: "/patch-notes", label: "Patch Notes", img: IMG.helmet, id: "patch" },
];

const useCountdown = (iso) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!iso) return;
    const tick = () => setLeft(Math.max(0, new Date(iso).getTime() - Date.now()));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [iso]);
  const s = Math.floor(left / 1000);
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
};

const pad = (n) => String(n).padStart(2, "0");

const useCountUp = (target, dur = 900) => {
  const [val, setVal] = useState(0);
  const ref = useState({ from: 0, raf: 0 })[0];
  useEffect(() => {
    const from = ref.from;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (target - from) * eased));
      if (p < 1) ref.raf = requestAnimationFrame(tick);
      else ref.from = target;
    };
    cancelAnimationFrame(ref.raf);
    ref.raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.raf);
  }, [target]); // eslint-disable-line
  return val;
};

const Home = () => {
  const { user } = useAuth();
  const [steamNudge, setSteamNudge] = useState(() => localStorage.getItem("wardogs_steam_nudge") !== "off");
  const [status, setStatus] = useState(null);
  const [showJoin, setShowJoin] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  const [posts, setPosts] = useState([]);
  const [clans, setClans] = useState([]);
  const [top, setTop] = useState([]);
  const [drops, setDrops] = useState([]);
  const [season, setSeason] = useState(null);
  const [servers, setServers] = useState(null);
  const [live, setLive] = useState(null);
  const cd = useCountdown(season?.ends_at);

  useEffect(() => {
    api.get("/server/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/server/players").then((r) => setLive(r.data)).catch(() => {});
    api.get("/forum/posts", { params: { sort: "hot" } }).then((r) => setPosts(r.data.slice(0, 15))).catch(() => {});
    api.get("/clans").then((r) => setClans(r.data.slice(0, 3))).catch(() => {});
    api.get("/leaderboard", { params: { sort: "xp", limit: 5 } }).then((r) => setTop(r.data)).catch(() => {});
    api.get("/loot/recent", { params: { limit: 6 } }).then((r) => setDrops(r.data)).catch(() => {});
    api.get("/season").then((r) => setSeason(r.data.season)).catch(() => {});
    api.get("/community/servers").then((r) => setServers(r.data.servers || [])).catch(() => {});
    const t = setInterval(() => {
      api.get("/server/status").then((r) => setStatus(r.data)).catch(() => {});
      api.get("/server/players").then((r) => setLive(r.data)).catch(() => {});
    }, 15000);
    const st = setInterval(() => api.get("/community/servers").then((r) => setServers(r.data.servers || [])).catch(() => {}), 45000);
    return () => { clearInterval(t); clearInterval(st); };
  }, []);

  const copyIp = () => { navigator.clipboard.writeText(SERVER_ID); toast.success("Join ID copied"); };

  const vote = async (e, id, current, value) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error("Sign in to vote"); return; }
    const next = current === value ? 0 : value;
    try {
      const { data } = await api.post(`/forum/posts/${id}/vote`, { value: next });
      setPosts((p) => p.map((x) => (x.id === id ? { ...x, score: data.score, _uv: next } : x)));
    } catch { toast.error("Vote failed"); }
  };

  const FACTIONS = [{ n: "VALKYRA", c: "#E4443A", logo: "/factions/valkyra.webp" }, { n: "LONESTAR", c: "#3498DB", logo: "/factions/lonestar.webp" }, { n: "MANTICORE", c: "#2ECC71", logo: "/factions/manticore.webp" }];
  const playerCount = useCountUp(status?.players ?? live?.count ?? 0);
  const isOnline = status?.status === "online";
  const SEED_THRESHOLD = 20;
  const livePlayers = status?.players ?? live?.count ?? 0;
  const maxPlayers = status?.max_players ?? 100;
  const seedState = !isOnline
    ? { label: "Server sleeping · here's how to join", color: "#E4443A", text: "#FFFFFF", cta: "How to Join" }
    : livePlayers === 0
    ? { label: "Empty — be the first, help seed!", color: "#FF9F1C", text: "#0B0C0E", cta: "Play Now" }
    : livePlayers < SEED_THRESHOLD
    ? { label: "Seeding — jump in and fill it up!", color: "#FF9F1C", text: "#0B0C0E", cta: "Play Now" }
    : { label: "Live — server active", color: "#2ECC71", text: "#0B0C0E", cta: "Play Now" };

  const playNow = async () => {
    try { await navigator.clipboard.writeText(SERVER_ID); setIdCopied(true); setTimeout(() => setIdCopied(false), 1500); toast.success("Server ID copied — follow the steps to join"); }
    catch (e) { toast.message("Open the join steps and copy the Server ID"); }
    setShowJoin(true);
  };

  const joinServer = async () => {
    try { await navigator.clipboard.writeText(SERVER_ID); } catch (e) { console.warn("clipboard write failed", e); }
    if (isOnline) { toast.success("Join ID copied — use it in the in-game Community Server Browser"); return; }
    toast.loading("Waking the server…", { id: "wake" });
    try {
      const { data } = await api.post("/server/wake");
      if (data.ok) toast.success("Server is booting — Join ID copied. It'll be joinable in ~1 min", { id: "wake" });
      else toast.error("Couldn't wake the server — Join ID copied, try connecting to wake it", { id: "wake" });
    } catch { toast.error("Couldn't reach the server — Join ID copied", { id: "wake" }); }
    setTimeout(() => api.get("/server/status").then((r) => setStatus(r.data)).catch(() => {}), 4000);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#D4A94A]/10">
        <div className="absolute inset-0">
          <video
            data-testid="hero-video"
            className="w-full h-full object-cover opacity-40 scale-105"
            src="/hero-video.mp4"
            poster="/hero-video-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0E]/40 via-[#0B0C0E]/70 to-[#0B0C0E]" />
          <div className="absolute inset-0 diag-stripes opacity-30" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-20 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="overline mb-5">OCE REGION // AU · NZ {status ? "· LIVE" : ""}</motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }} className="mb-4 flex items-center justify-center">
            <img src="/oce-dogs-logo.webp" alt="OCE DOGS" data-testid="hero-logo" className="w-auto h-36 sm:h-48 lg:h-60 drop-shadow-[0_8px_30px_rgba(0,0,0,0.6)]" />
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }} data-testid="hero-h1" className="font-heading font-black uppercase text-2xl sm:text-3xl text-[#F1F5F9] mb-3">OCE DOGS <span className="text-[#D4A94A]">— Oceanic WARDOGS Community</span></motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.5 }} className="text-[#A0A0A0] text-base sm:text-lg max-w-2xl mx-auto mb-7">
            Database, interactive maps, leaderboards, season pass, loot drops and more — the Oceanic HQ for WARDOGS operators.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="flex flex-wrap justify-center gap-3 mb-10">
            {user ? (
              <Link data-testid="hero-cta-season" to="/season" className="inline-flex items-center gap-2 bg-[#D4A94A] hover:bg-[#E5BD60] text-black font-stencil font-black uppercase tracking-wide px-6 py-3 tactical-frame-sm glow-orange transition-colors"><Flag className="w-4 h-4" /> Season Pass <ArrowRight className="w-4 h-4" /></Link>
            ) : (
              <Link data-testid="hero-cta-enlist" to="/auth?mode=register" className="inline-flex items-center gap-2 bg-[#D4A94A] hover:bg-[#E5BD60] text-black font-stencil font-black uppercase tracking-wide px-6 py-3 tactical-frame-sm glow-orange transition-colors">Link your Wardogs profile <ArrowRight className="w-4 h-4" /></Link>
            )}
            <button data-testid="hero-copy-ip" onClick={copyIp} className="inline-flex items-center gap-2 glass hover:border-[#D4A94A]/60 text-[#F1F5F9] font-mono text-sm px-6 py-3 tactical-frame-sm transition-colors"><Copy className="w-4 h-4" /> Copy Join ID</button>
          </motion.div>

          {/* PLAY NOW — one-tap steam connect with live pop + seeding state */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.5 }} className="flex flex-col items-center gap-2 mb-10">
            <button
              data-testid="hero-play-now"
              onClick={playNow}
              className="group relative inline-flex items-center gap-4 font-stencil font-black uppercase px-7 sm:px-9 py-4 tactical-frame-sm transition-transform hover:scale-[1.02] active:scale-95"
              style={{ background: seedState.color, color: seedState.text, boxShadow: `0 0 34px ${seedState.color}55` }}
            >
              <span className="relative flex h-2.5 w-2.5">
                {isOnline && <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70" style={{ background: seedState.text }} />}
                <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: seedState.text }} />
              </span>
              <span className="flex items-center gap-2 text-lg tracking-wide">{idCopied ? <><Check className="w-6 h-6 animate-ping-once text-[#0B0C0E]" data-testid="hero-play-now-check" /> ID Copied!</> : <><Gamepad2 className="w-6 h-6" /> {seedState.cta}</>}</span>
              <span className="h-9 w-px" style={{ background: `${seedState.text}33` }} />
              <span className="flex flex-col items-start leading-tight text-left">
                <span data-testid="hero-play-now-pop" className="text-xl font-heading tabular-nums">{isOnline ? `${playerCount}/${maxPlayers}` : "OFFLINE"}</span>
                <span data-testid="hero-play-now-state" className="text-[10px] font-mono normal-case tracking-normal opacity-85">{seedState.label}</span>
              </span>
            </button>
            <button data-testid="hero-play-now-fallback" onClick={() => setShowJoin(true)} className="font-mono text-[11px] text-[#64748B] hover:text-[#D4A94A] transition-colors inline-flex items-center gap-1.5">
              <Gamepad2 className="w-3 h-3" /> How do I join OCE DOGS? →
            </button>
          </motion.div>
          <JoinModal open={showJoin} onClose={() => setShowJoin(false)} />

          {/* Season countdown */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.5 }} data-testid="hero-season-countdown" className="max-w-3xl mx-auto glass hud-4 p-6 sm:p-8">
            <span className="hud-tr" /><span className="hud-bl" />
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#D4A94A]/50" />
              <div className="overline">{season ? `${season.name} · ends in` : "Season loading"}</div>
              <span className="h-px w-10 bg-[#D4A94A]/50" />
            </div>
            <div className="grid grid-cols-4 gap-2 sm:gap-6">
              {[["Days", cd.d], ["Hours", cd.h], ["Minutes", cd.m], ["Seconds", cd.s]].map(([l, v]) => (
                <div key={l} className="relative">
                  <div className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-glow-orange tabular-nums">{pad(v)}</div>
                  <div className="overline text-[9px] mt-1 opacity-80">{l}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 border-t border-[#2A2E3D] pt-4 font-mono text-[11px] text-[#94A3B8]">
              <div data-testid="hero-stat-server"><span className="mr-1.5 inline-block w-2 h-2 rounded-full align-middle" style={{ background: status?.status === "online" ? "#2ECC71" : "#E4443A" }} />SERVER <span className="text-[#F1F5F9]">{status?.status === "online" ? `${status.players ?? "—"}/${status.max_players}` : "OFFLINE"}</span></div>
              <div data-testid="hero-stat-tickrate">TICK <span className="text-[#F1F5F9]">{status?.tickrate ?? 128}Hz</span> · PING <span className="text-[#F1F5F9]">{status?.ping_ms ?? 22}ms</span></div>
              <div data-testid="hero-stat-map">MAP <span className="text-[#F1F5F9]">{status?.status === "online" ? (status?.current_map ?? "—") : "—"}</span></div>
              <div data-testid="hero-stat-uptime">UPTIME <span className="text-[#F1F5F9]">{status?.uptime_hours ?? 0}h</span></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* LIVE SERVER — big top-center hero */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 -mt-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          data-testid="home-live-widget"
          className="relative overflow-hidden glass tactical-frame p-6 lg:p-8 border border-[#D4A94A]/25 hud-4"
        >
          <span className="hud-tr" /><span className="hud-bl" />
          <div className="relative flex items-start justify-between flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                {isOnline && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2ECC71] opacity-60" />}
                <span className="relative inline-flex rounded-full h-3 w-3" style={{ background: isOnline ? "#2ECC71" : "#E4443A" }} />
              </span>
              <div>
                <div className="overline"><Radio className="inline w-3 h-3 mr-1" /> {isOnline ? "Live // Community Server" : "OCE Community Server"}
                  {status?.source === "bisect" && <span className="text-[#64748B] normal-case tracking-normal ml-1" title="Live server state via BisectHosting">· {status.server_state}</span>}
                  {status?.source === "simulated" && <span className="text-[#64748B] normal-case tracking-normal ml-1" title="Live feed coming soon — showing demo data">· demo feed</span>}
                </div>
                <h2 className="font-heading text-3xl lg:text-4xl font-black uppercase flex items-center gap-3 leading-none mt-1">
                  <MapPin className="w-6 h-6 text-[#D4A94A]" /> {isOnline ? (status?.current_map || "In Match") : (status?.name || "OCE DOGS #1 | KOTH | OCEDOGS.COM")}
                </h2>
                {isOnline ? (
                  <div className="font-mono text-xs text-[#94A3B8] mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className="text-[#F1F5F9]">{status?.mode || "King of the Hill"}</span>
                    <span className="text-[#64748B]">·</span>
                    <span className="inline-flex items-center gap-1"><Target className="w-3 h-3 text-[#D4A94A]" /> {status?.control_zone || "—"}</span>
                  </div>
                ) : (
                  <div className="font-mono text-xs text-[#64748B] mt-1.5">Join ID: <span className="text-[#D4A94A]">{SERVER_ID}</span></div>
                )}
              </div>
            </div>
            <div className="text-right">
              {isOnline ? (
                <>
                  <div className="font-heading font-black text-4xl lg:text-5xl tabular-nums text-glow-orange" data-testid="home-live-playercount">{playerCount}<span className="text-[#64748B] text-2xl">/{status?.max_players ?? 100}</span></div>
                  <div className="overline text-[9px]">Operators deployed</div>
                </>
              ) : (
                <>
                  <div className="font-heading font-black text-3xl lg:text-4xl uppercase text-[#E4443A]" data-testid="home-live-playercount">Offline</div>
                  <div className="overline text-[9px]">Sleeping · wakes on connect</div>
                </>
              )}
            </div>
          </div>

          {isOnline ? (
          <>
          {status?.hot_zone && (
            <div className="relative mb-5 text-xs font-mono bg-[#FF9F1C] text-black px-3 py-1.5 inline-flex items-center gap-2 font-black uppercase tracking-wide">
              <Flame className="w-4 h-4" /> Hot Zone Active · 2× Cash
            </div>
          )}

          <div className="relative space-y-3 mb-6">
            {FACTIONS.map((f) => {
              const limit = status?.score_limit ?? 100;
              const raw = (status?.team_scores || live?.team_scores)?.[f.n] ?? 0;
              const sc = raw;
              const pct = Math.min(100, (raw / limit) * 100);
              return (
                <div key={f.n} className="flex items-center gap-3" data-testid={`home-faction-${f.n}`}>
                  <img src={f.logo} alt="" className="w-6 h-6 lg:w-7 lg:h-7 object-contain shrink-0" />
                  <span className="w-20 lg:w-24 font-heading font-black uppercase text-sm lg:text-base" style={{ color: f.c }}>{f.n}</span>
                  <div className="flex-1 h-4 bg-[#0B0C0E] border border-[#2A2E3D] relative overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease: "easeOut" }} className="h-full relative overflow-hidden" style={{ background: f.c }}>
                      <span className="bar-glint" />
                    </motion.div>
                  </div>
                  <span className="w-10 text-right font-heading font-black text-lg tabular-nums" style={{ color: f.c }}>{sc}</span>
                </div>
              );
            })}
          </div>

          <div className="relative mb-5 -mt-1">
            <div className="overline text-[9px] mb-1">Player activity · last 12h</div>
            <PlayerActivity hours={12} compact callout />
          </div>
          </>
          ) : (
            <div className="relative mb-6 p-6 bg-[#0B0C0E]/60 border border-[#E4443A]/25 tactical-frame-sm text-center" data-testid="home-server-offline">
              <div className="font-heading font-black text-2xl uppercase text-[#E4443A]">Server {status?.server_state === "starting" ? "Booting" : "Offline"}</div>
              <div className="font-mono text-xs text-[#94A3B8] mt-2 max-w-md mx-auto">{status?.sleeping_note || "The server is currently offline."} WARDOGS Early Access launches Sept 10 — hit Join Server to wake it and drop in.</div>
            </div>
          )}

          <div className="relative flex items-center justify-between flex-wrap gap-4 border-t border-[#2A2E3D] pt-5">
            <div className="font-mono text-xs text-[#94A3B8] flex items-center gap-4 flex-wrap">
              <span><Flag className="inline w-3.5 h-3.5 mr-1 text-[#D4A94A]" /> First faction to {status?.score_limit ?? 100}</span>
              <span>SCORE TICK {status?.score_tick?.seconds ?? 24}s</span>
              {status?.next_map && <span>NEXT <span className="text-[#F1F5F9]">{status.next_map}</span></span>}
              <span>PING {status?.ping_ms ?? 22}ms</span>
            </div>
            <div className="flex gap-2">
              <button data-testid="home-join-server" onClick={joinServer} className="bg-[#D4A94A] hover:bg-[#E5BD60] text-black font-stencil font-black uppercase text-sm px-6 py-3 tactical-frame-sm glow-orange inline-flex items-center gap-2 transition-colors"><Copy className="w-4 h-4" /> {isOnline ? "Join Server" : "Wake & Join"}</button>
              <Link to="/live" data-testid="home-live-link" className="px-5 py-3 border border-[#2A2E3D] hover:border-[#D4A94A] font-stencil font-bold uppercase text-sm tactical-frame-sm inline-flex items-center gap-2 transition-colors">Details <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>

        </motion.div>
      </section>

      {/* SUPPORT THE SERVER — donation thermometer */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-6">
        <LiveKillTicker />
      </section>

      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-6">
        <LootTicker />
      </section>

      {/* STEAM LINK NUDGE — signed-in members who haven't linked Steam */}
      {user && !user.steam_id && steamNudge && (
        <section className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-6">
          <div data-testid="home-steam-nudge" className="relative flex items-center gap-3 flex-wrap bg-gradient-to-r from-[#1b2838] to-[#13151B] border border-[#D4A94A]/40 tactical-frame-sm px-4 py-3">
            <Gamepad2 className="w-5 h-5 text-[#D4A94A] shrink-0" />
            <div className="flex-1 min-w-[220px]">
              <div className="font-stencil font-black uppercase text-sm text-[#F1F5F9]">Link Steam to track your kills</div>
              <div className="font-mono text-[11px] text-[#94A3B8] mt-0.5">The server tracks players by Steam ID — link before you play so your kills, wins and XP land on your profile. <span className="text-[#64748B]">Kills earned before linking aren't backfilled.</span></div>
            </div>
            <Link to="/profile" data-testid="home-steam-nudge-link" className="inline-flex items-center gap-2 bg-[#1b2838] hover:bg-[#2a3f5f] text-white font-stencil font-bold uppercase text-xs px-4 py-2 tactical-frame-sm shrink-0"><Gamepad2 className="w-4 h-4" /> Link Steam</Link>
            <button data-testid="home-steam-nudge-dismiss" onClick={() => { setSteamNudge(false); localStorage.setItem("wardogs_steam_nudge", "off"); }} title="Dismiss" className="absolute top-1.5 right-1.5 text-[#64748B] hover:text-[#F1F5F9] transition-colors"><X className="w-4 h-4" /></button>
          </div>
        </section>
      )}

      {/* TEAMPLAY LEADERS — role ranks strip (hidden until real data) */}
      <RoleRanksStrip />

      <FeaturedYouTube />

      {/* CONTENT */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 mt-6 pb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass tactical-frame p-6 hud-corners min-w-0">
          <div className="flex items-center justify-between mb-5">
            <div><div className="overline mb-1"><Flame className="inline w-3 h-3 mr-1" /> Hot on the forum</div><h2 className="font-heading text-2xl font-extrabold uppercase">Latest Intel</h2></div>
            <Link data-testid="home-forum-viewall" to="/forum" className="text-sm font-stencil uppercase text-[#D4A94A] hover:text-[#E5BD60]">View all →</Link>
          </div>
          <div className="space-y-1.5">
            {posts.map((p, i) => (
              <div key={p.id} data-testid={`home-post-${i}`} className="flex items-center gap-3 p-3 hover:bg-[#1A1D26] border border-transparent hover:border-[#2A2E3D] transition-colors">
                <div className="flex flex-col items-center w-8 shrink-0">
                  <button data-testid={`home-upvote-${p.id}`} onClick={(e) => vote(e, p.id, p._uv || 0, 1)} className={`p-0.5 hover:text-[#D4A94A] transition-colors ${p._uv === 1 ? "text-[#D4A94A]" : "text-[#64748B]"}`}>
                    <ArrowUp className="w-4 h-4" strokeWidth={2.6} />
                  </button>
                  <div className="font-heading text-base font-black leading-none tabular-nums text-[#F1F5F9]">{p.score}</div>
                  <button data-testid={`home-downvote-${p.id}`} onClick={(e) => vote(e, p.id, p._uv || 0, -1)} className={`p-0.5 hover:text-[#E4443A] transition-colors ${p._uv === -1 ? "text-[#E4443A]" : "text-[#64748B]"}`}>
                    <ArrowDown className="w-4 h-4" strokeWidth={2.6} />
                  </button>
                </div>
                <Link to={`/forum/${p.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                  <DogTag frameId={p.author_frame} avatarUrl={p.author_avatar_url} callsign={p.author_callsign} size={34} isSupporter={p.author_is_supporter} />
                  <div className="flex-1 min-w-0">
                    <div className="font-stencil font-bold text-sm uppercase tracking-wide truncate">{p.title}</div>
                    <div className="font-mono text-xs text-[#64748B] mt-0.5 flex items-center gap-2 flex-wrap"><span className="text-[#D4A94A]">[{p.category}]</span> {p.author_callsign} {p.author_faction && <FactionCrest faction={p.author_faction} size={14} />}{p.author_clan_tag && <span className="inline-flex items-center gap-0.5 bg-[#2A2E3D] text-[#D4A94A] px-1 py-0.5"><Shield className="w-2.5 h-2.5" />[{p.author_clan_tag}]</span>}<span className="text-[#2ECC71] inline-flex items-center gap-0.5"><DollarSign className="w-2.5 h-2.5" />{(p.author_cash ?? 0).toLocaleString()}</span><BannerStrip bannerId={p.author_banner} compact /> · {p.comment_count} comments</div>
                  </div>
                </Link>
              </div>
            ))}
            {posts.length === 0 && <div className="text-sm text-[#64748B] font-mono">// NO POSTS</div>}
          </div>
        </div>

        <div className="space-y-4">
          <DiscordCard />
          <DonationThermometer />
          <Link to="/community" data-testid="home-servers-widget" className="block glass tactical-frame p-6 hover:border-[#D4A94A]/60 border border-transparent transition-colors group">
            <div className="flex items-center justify-between mb-4">
              <div><div className="overline mb-1"><Server className="inline w-3 h-3 mr-1" /> OCE DOGS · live</div><h2 className="font-heading text-xl font-extrabold uppercase">Our Servers</h2></div>
              <span className="live-dot" />
            </div>
            {servers ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#0B0C0E]/60 border border-[#2A2E3D] tactical-frame-sm p-3">
                    <div className="font-heading font-black text-2xl text-[#D4A94A] tabular-nums" data-testid="home-oce-players">{servers.reduce((n, s) => n + (s.players || 0), 0).toLocaleString()}</div>
                    <div className="overline text-[9px]">Players online</div>
                  </div>
                  <div className="bg-[#0B0C0E]/60 border border-[#2A2E3D] tactical-frame-sm p-3">
                    <div className="font-heading font-black text-2xl text-[#F1F5F9] tabular-nums">{servers.filter((s) => s.online).length}</div>
                    <div className="overline text-[9px]">Servers online</div>
                  </div>
                </div>
                <div className="mt-3 font-mono text-[11px] text-[#64748B] flex items-center justify-between">
                  <span>{servers.length} community server{servers.length === 1 ? "" : "s"}</span>
                  <span className="group-hover:text-[#D4A94A] transition-colors">View servers →</span>
                </div>
              </>
            ) : <div className="font-mono text-xs text-[#64748B]">// loading server feed…</div>}
          </Link>
          <div className="glass tactical-frame p-6">
            <div className="flex items-center justify-between mb-4"><div><div className="overline mb-1"><TrendingUp className="inline w-3 h-3 mr-1" /> Top ops</div><h2 className="font-heading text-xl font-extrabold uppercase">Top Clans</h2></div><Link to="/clans" className="text-sm font-stencil uppercase text-[#D4A94A]">All →</Link></div>
            <div className="space-y-2">
              {clans.map((c, i) => (
                <Link key={c.id} to={`/clans/${c.id}`} data-testid={`home-clan-${i}`} className="flex items-center gap-3 p-2.5 bg-[#0B0C0E]/60 border border-[#2A2E3D] hover:border-[#D4A94A] tactical-frame-sm transition-colors">
                  <div className="w-9 h-9 bg-[#D4A94A] flex items-center justify-center font-heading font-black text-black tactical-frame-sm">{i + 1}</div>
                  <div className="flex-1 min-w-0"><div className="font-stencil font-bold uppercase text-sm truncate">[{c.tag}] {c.name}</div><div className="font-mono text-[11px] text-[#94A3B8]">{c.stats?.xp?.toLocaleString()} XP · {c.stats?.win_rate}% WR</div></div>
                </Link>
              ))}
            </div>
          </div>
          <div className="glass tactical-frame p-6">
            <div className="flex items-center justify-between mb-4"><div><div className="overline mb-1"><Gift className="inline w-3 h-3 mr-1" /> Recent</div><h2 className="font-heading text-xl font-extrabold uppercase">Loot Drops</h2></div><Link to="/loot" className="text-sm font-stencil uppercase text-[#D4A94A]">All →</Link></div>
            <div className="space-y-1.5">
              {drops.map((d) => (
                <div key={d.id} data-testid={`home-drop-${d.id}`} className="flex items-center gap-2 text-xs font-mono">
                  <span className="w-2 h-2" style={{ background: RARITY[d.rarity]?.hex }} />
                  <span className="text-[#F1F5F9] truncate">{d.callsign}</span>
                  <span className="text-[#64748B]">got</span>
                  <span className="truncate" style={{ color: RARITY[d.rarity]?.hex }}>{d.name}</span>
                </div>
              ))}
              {drops.length === 0 && <div className="text-sm text-[#64748B] font-mono">// NO DROPS YET</div>}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 glass tactical-frame p-6">
          <div className="flex items-center justify-between mb-5"><div><div className="overline mb-1">// Global rank</div><h2 className="font-heading text-2xl font-extrabold uppercase">Top Operators</h2></div><Link to="/leaderboard" className="text-sm font-stencil uppercase text-[#D4A94A]">Full board →</Link></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
            {top.map((u, i) => (
              <motion.div key={u.id} whileHover={{ y: -3 }} data-testid={`home-top-${i}`} className="bg-[#0B0C0E]/60 border border-[#2A2E3D] hover:border-[#D4A94A]/60 p-4 tactical-frame-sm transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <DogTag frameId={u.equipped_frame} avatarUrl={u.avatar_url} callsign={u.callsign} size={44} isSupporter={u.is_supporter} />
                  <div className="min-w-0"><div className={`font-heading font-black text-xs ${i === 0 ? "text-[#D4A94A]" : "text-[#94A3B8]"}`}>#{i + 1}</div><div className="font-stencil font-bold uppercase truncate">{u.callsign}</div></div>
                </div>
                <BannerStrip bannerId={u.equipped_banner} compact className="mb-2" />
                <div className="font-mono text-[11px] text-[#64748B]">LVL {u.stats?.level} · KD {u.stats?.kd?.toFixed(2)} · {u.stats?.role}</div>
                <div className="mt-3 h-1 bg-[#2A2E3D]"><div className="h-full bg-[#D4A94A]" style={{ width: `${Math.min(100, (u.stats?.xp || 0) / 700)}%` }} /></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE TILES — moved below Latest Intel */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TILES.map((t, i) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ delay: i * 0.05, duration: 0.4 }}>
              <Link to={t.to} data-testid={`feature-tile-${t.id}`} className="tile group block h-28 sm:h-32 tactical-frame border border-[#2A2E3D] hover:border-[#D4A94A]/70 bg-[#13151B] transition-colors">
                <img src={t.img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C0E]/90 via-[#0B0C0E]/50 to-[#0B0C0E]/30" />
                <div className="tile-shine" />
                <div className="relative h-full flex items-center px-6">
                  <span className="font-heading font-black uppercase text-lg sm:text-xl tracking-tight text-[#F1F5F9] group-hover:text-[#D4A94A] transition-colors">{t.label}</span>
                  {t.hot && <span className="ml-3 text-[9px] font-mono font-bold tracking-widest bg-[#D4A94A] text-black px-1.5 py-0.5">HOT</span>}
                  <ArrowRight className="ml-auto w-5 h-5 text-[#64748B] group-hover:text-[#D4A94A] group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
